import jsPDF from "jspdf";
import { format } from "date-fns";
import { parseDateOnly } from "./parseDateOnly";

/* ────────────────────────────────────────────────────────────────────────────
 * Golf Getaway Pass — PDF voucher
 *
 * Matches the client-approved preview: dark hero with gold "EXQURA" wordmark
 * + gold ticket badge + outlined reservation pill, a two-up "Your Getaway" /
 * "Your Getaway Summary" stat row, a tinted "Your Golfing Group" card with
 * one name per line, a per-day itinerary (dark day pill + bold type/plain
 * description rows), a dark-headed "Important Instructions" card, a dark-
 * headed "Important Visuals" gallery with captions under every photo, and a
 * full-bleed dark footer band with the "EXQURA" wordmark + support contact.
 * ──────────────────────────────────────────────────────────────────────────── */

const PAGE_W = 210; // A4 mm
const PAGE_H = 297;
const MARGIN = 14;
const CONTENT_W = PAGE_W - MARGIN * 2;

const GAP = 6; // vertical space between cards — one spacing scale, used everywhere
const PAD = 7; // inner card padding
const RADIUS = 3.5;
const FOOTER_H = 16;
const LH = (pt) => pt * 0.45; // text line height in mm for a given point size

// Brand palette — same values the web app uses (exqura-color-codes.csv).
const DEEP = [11, 36, 33]; // #0B2421 primary
const GOLD = [212, 175, 55]; // #D4AF37 accent
const BG = [247, 248, 247]; // #F7F8F7 page background
const CARD = [255, 255, 255];
const BORDER = [229, 231, 235]; // #E5E7EB
const GRAY = [107, 114, 128]; // #6B7280 secondary text
const GRAY_SOFT = [156, 163, 175]; // #9CA3AF
const INK = [55, 65, 81]; // #374151 body text
const TINT = [250, 245, 231]; // gold @ ~12% on white — "Your Golfing Group" card
const TINT_BORDER = [233, 213, 160];
const PILL_BG = [41, 57, 36]; // gold @ ~15% over DEEP — hero reservation pill
const PILL_BORDER = [120, 110, 60];
const GREEN_TINT = [233, 240, 230]; // light sage green — Itinerary card background
const GREEN_BORDER = [206, 220, 199];

const ORDINAL_WORDS = [
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
];

const DUMMY_IMAGES = [
  {
    url: "https://placehold.co/300x300?text=Image+1",
    caption: "Tee Off at the 1st Hole",
  },
  {
    url: "https://placehold.co/300x300?text=Image+2",
    caption: "Practice Green",
  },
  {
    url: "https://placehold.co/300x300?text=Image+3",
    caption: "Flag on the Green",
  },
  {
    url: "https://placehold.co/300x300?text=Image+4",
    caption: "Meet Your Caddie",
  },
  {
    url: "https://placehold.co/300x300?text=Image+5",
    caption: "Fairway with a View",
  },
];

const DEFAULT_INSTRUCTIONS = [
  "Please arrive at least 30 minutes before your first scheduled tee time or ferry departure.",
  "This Golf Getaway Pass and a valid passport must be presented at ferry check-in.",
  "Golf attire and soft spikes are required at every course on this itinerary.",
  "Need to reschedule or change any details? Contact us at customerservice@exqura.golf and we'll sort it out.",
  "Weather delays may shift tee times slightly — we'll notify you by email if anything changes.",
];

/* ── Input normalisation ── */

function normalizeInstructions(raw) {
  if (Array.isArray(raw)) {
    const cleaned = raw.map((s) => String(s).trim()).filter(Boolean);
    return cleaned.length ? cleaned : DEFAULT_INSTRUCTIONS;
  }
  if (typeof raw === "string" && raw.trim()) {
    const list = raw
      .split(/\r?\n+/)
      .map((l) => l.trim())
      .filter(Boolean);
    return list.length ? list : DEFAULT_INSTRUCTIONS;
  }
  return DEFAULT_INSTRUCTIONS;
}

/**
 * Accepts either plain URL strings or { url, caption } objects so the admin
 * can supply a caption per photo (falls back to no caption, or the sample
 * captions when nothing at all is provided).
 */
function normalizeImages(raw) {
  const arr = Array.isArray(raw) ? raw.filter(Boolean) : [];
  const list = arr.length
    ? arr.map((item) =>
        typeof item === "string"
          ? { url: item, caption: "" }
          : { url: item.url, caption: item.caption || "" },
      )
    : DUMMY_IMAGES;
  return list.slice(0, 5);
}

/** jsPDF needs an explicit format string — guessing wrong makes addImage throw. */
function dataUriFormat(dataUri) {
  const match = /^data:image\/(png|jpe?g|webp|gif)/i.exec(dataUri || "");
  const ext = (match?.[1] || "png").toLowerCase();
  if (ext === "jpg" || ext === "jpeg") return "JPEG";
  if (ext === "webp") return "WEBP";
  if (ext === "gif") return "GIF";
  return "PNG";
}

/** Draws an image contained (aspect-ratio preserved, centered) inside a box. */
function addContainedImage(doc, dataUri, x, y, boxW, boxH) {
  const props = doc.getImageProperties(dataUri);
  let w = boxW;
  let h = (props.height / props.width) * w;
  if (h > boxH) {
    h = boxH;
    w = (props.width / props.height) * h;
  }
  doc.addImage(
    dataUri,
    dataUriFormat(dataUri),
    x + (boxW - w) / 2,
    y + (boxH - h) / 2,
    w,
    h,
  );
}

async function urlToDataUri(url) {
  if (typeof url === "string" && url.startsWith("data:")) return url;
  try {
    const res = await fetch(url, { mode: "cors" });
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.warn(`Could not load image for PDF: ${url}`, err);
    return null;
  }
}

/**
 * Downscales + re-encodes an image data URI as JPEG so embedded photos don't
 * bloat the PDF. Gallery photos in this layout only ever render inside a
 * small (~30mm × ~53mm) box, so there's no reason to ship a multi-megapixel
 * source image inside the file. Runs entirely client-side via canvas; falls
 * back to the original data URI if anything goes wrong (e.g. a CORS-tainted
 * canvas, or running outside a browser).
 *
 * @param {string} dataUri
 * @param {number} maxDim   - longest edge, in px, to downscale to
 * @param {number} quality  - JPEG quality 0..1
 */
async function compressImageDataUri(dataUri, maxDim = 480, quality = 0.72) {
  if (!dataUri || typeof document === "undefined") return dataUri;
  try {
    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = dataUri;
    });
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", quality);
  } catch (err) {
    console.warn("Could not compress image for PDF, using original", err);
    return dataUri;
  }
}

function toDate(dateVal) {
  if (!dateVal) return null;
  try {
    const d = parseDateOnly(dateVal);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

/** Compact date, e.g. "24 Sep 2026" (or "24 Sep" when withYear is false). */
function formatShort(dateVal, withYear = true) {
  const d = toDate(dateVal);
  if (!d) return "";
  return format(d, withYear ? "dd MMM yyyy" : "dd MMM");
}

/** Date used on day pills, e.g. "04 Sep 2026". */
function formatDateLong(dateVal) {
  const d = toDate(dateVal);
  if (!d) return "";
  return format(d, "EEEE, dd MMM yyyy");
}


// Accepts "0820", "08:20", "8:20 AM", "945", "2:45 pm" → minutes since midnight.
function parseMinutes(str) {
  const m = /^\s*(\d{1,2})[:.]?(\d{2})\s*(am|pm)?\s*$/i.exec(String(str ?? ""));
  if (!m) return null;
  let h = Number(m[1]);
  const min = Number(m[2]);
  const ap = m[3]?.toLowerCase();
  if (ap === "pm" && h < 12) h += 12;
  if (ap === "am" && h === 12) h = 0;
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

// Minutes → "08:20 (ID)". For "0820 (ID)" style, drop the ":" below.
function fmtTime(mins, zone) {
  if (mins == null) return "";
  const t = ((mins % 1440) + 1440) % 1440;
  const hh = String(Math.floor(t / 60)).padStart(2, "0");
  const mm = String(t % 60).padStart(2, "0");
  return `${hh}:${mm} (${zone})`;
}

/**
 * Best-effort day-by-day itinerary built from booking fields. Each round is
 * a "day"; ferry/transport legs attach to the first/last day. Every item now
 * carries a separate `type` (bold lead-in, e.g. "Round 1 Golf", "Transfer")
 * and `desc` (plain description) so they can be rendered in two weights on
 * the same line, matching the approved layout.
 */
function buildItinerary(booking) {
  const f = booking.selectedFerry;
  const SG = "SG";
  const ID = "ID";
  const origin = f?.originport || "Singapore";
  const dest = f?.destination || "Batam";

  const dayMap = new Map(); // yyyy-MM-dd -> { date, rounds: [] }
  const ensureDay = (dateVal) => {
    const d = toDate(dateVal);
    if (!d) return null;
    const k = format(d, "yyyy-MM-dd");
    if (!dayMap.has(k)) dayMap.set(k, { date: d, rounds: [] });
    return dayMap.get(k);
  };

  // One entry per round, attached to its own date
  (booking.selectedCourses || []).forEach((course, i) => {
    const day = ensureDay(booking.roundDates?.[i]);
    if (!day) return;
    day.rounds.push({
      n: i + 1,
      name: course?.name || "Golf Course",
      tee: parseMinutes(booking.roundTimes?.[i]),
    });
  });

  // Arrival / departure days that have no golf still get a day card
  if (booking.selectedAccommodation) {
    ensureDay(booking.checkInDate);
    ensureDay(booking.checkOutDate);
  }
  // Safety net if nothing has a valid date
  if (dayMap.size === 0) ensureDay(booking.checkInDate);

  const days = Array.from(dayMap.values()).sort((a, b) => a.date - b.date);
  const ferryIn = f ? parseMinutes(booking.selectedFerryTime) : null; // SG departure
  const ferryOut = f ? parseMinutes(booking.selectedOutboundFerryTime) : null; // Batam departure

  return days
    .map((day, i) => {
      const first = i === 0;
      const last = i === days.length - 1;
      const rounds = [...day.rounds].sort((a, b) => (a.tee ?? 0) - (b.tee ?? 0));
      const firstRound = rounds[0] || null;
      const lastRound = rounds[rounds.length - 1] || null;
      const items = [];
      const add = (mins, zone, type, desc = "") => {
        if (mins != null) items.push({ time: fmtTime(mins, zone), type, desc });
      };

      // Arrival
      if (first && ferryIn != null) {
        add(ferryIn, SG, "Ferry Depart", `${origin} to ${dest}`);
        add(ferryIn, ID, "Ferry Arrival", dest);
        add(
          ferryIn + 30,
          ID,
          "Transfer",
          `Ferry Terminal to ${firstRound ? "Golf Course" : "Hotel"}`,
        );
      } else if (firstRound?.tee != null) {
        add(firstRound.tee - 60, ID, "Transfer", "Hotel to Golf Course");
      }

      // Golf
      rounds.forEach((r) => {
        items.push({
          time: r.tee != null ? fmtTime(r.tee, ID) : "",
          type: `Round ${r.n} Golf`,
          desc: r.name,
        });
      });

      if (last) {
        if (ferryOut != null) {
          add(
            ferryOut - 90,
            ID,
            "Transfer",
            `${lastRound ? "Golf Course" : "Hotel"} to Ferry Terminal`,
          );
          add(ferryOut, ID, "Ferry Depart", `${dest} to ${origin}`);
          add(ferryOut + 120, SG, "Ferry Arrival", origin);
          add(ferryOut + 135, SG, "Golf Getaway Complete");
        }
      } else if (lastRound?.tee != null) {
        add(lastRound.tee + 300, ID, "Transfer", "Golf Course to Hotel");
        add(lastRound.tee + 330, ID, "Day Complete");
      }

      return { date: day.date, items };
    })
    .filter((d) => d.items.length)
    .map((d, i) => ({
      dayLabel: `Day ${ORDINAL_WORDS[i] || i + 1}: ${formatDateLong(d.date)}`,
      items: d.items,
    }));
}
/* ────────────────────────────── draw primitives ─────────────────────────── */

function setFont(doc, style, size, color) {
  doc.setFont("helvetica", style);
  doc.setFontSize(size);
  doc.setTextColor(...(color || DEEP));
}

function eyebrow(doc, text, x, y, color) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.8);
  doc.setTextColor(...(color || GOLD));
  doc.setCharSpace(0.22);
  doc.text(String(text).toUpperCase(), x, y);
  doc.setCharSpace(0);
}

/** White rounded card with a hairline border. */
function panel(doc, x, y, w, h, opts = {}) {
  doc.setFillColor(...(opts.fill || CARD));
  doc.setDrawColor(...(opts.border || BORDER));
  doc.setLineWidth(opts.lw ?? 0.3);
  doc.roundedRect(x, y, w, h, opts.r ?? RADIUS, opts.r ?? RADIUS, "FD");
}

const card = (doc, y, h, opts) => panel(doc, MARGIN, y, CONTENT_W, h, opts);

/** Wraps text to a width, leaving font state set to (style, size). */
function wrap(doc, text, width, size, style = "normal") {
  doc.setFont("helvetica", style);
  doc.setFontSize(size);
  return doc.splitTextToSize(String(text ?? ""), width);
}

function drawLines(doc, arr, x, y, size) {
  arr.forEach((ln, i) => doc.text(ln, x, y + i * LH(size)));
}

/**
 * Draws a bold "Type - " lead-in followed by a plain-weight description,
 * wrapping the description within `width`. Returns the number of lines used
 * so the caller can advance its cursor.
 */
function drawTypeDesc(doc, type, desc, x, y, width, size = 8.5) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(size);
  doc.setTextColor(...DEEP);
  const lead = desc ? `${type} - ` : type; // in drawTypeDesc
  const leadW = doc.getTextWidth(lead);
  doc.text(lead, x, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(size);
  doc.setTextColor(...INK);
  const firstLineW = Math.max(width - leadW, 12);
  const descLines = doc.splitTextToSize(String(desc || ""), firstLineW);
  doc.text(descLines[0] || "", x + leadW, y);

  const rest = descLines.slice(1);
  if (!rest.length) return 1;
  const restWrapped = doc.splitTextToSize(rest.join(" "), width);
  let ny = y;
  restWrapped.forEach((ln) => {
    ny += LH(size);
    doc.text(ln, x, ny);
  });
  return 1 + restWrapped.length;
}

/** Measures how many lines drawTypeDesc will need, without drawing anything. */
function measureTypeDescLines(doc, type, desc, width, size = 8.5) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(size);
  const leadW = doc.getTextWidth(desc ? `${type} - ` : type);
  doc.setFont("helvetica", "normal");
  const firstLineW = Math.max(width - leadW, 12);
  const descLines = doc.splitTextToSize(String(desc || ""), firstLineW);
  const rest = descLines.slice(1);
  if (!rest.length) return 1;
  return 1 + doc.splitTextToSize(rest.join(" "), width).length;
}

/* ── icon glyphs: stroked in a normalised 0..1 box; caller sets colour/width ─ */

function stroke(doc, color, w) {
  doc.setDrawColor(...color);
  doc.setLineWidth(w);
  doc.setLineCap("round");
  doc.setLineJoin("round");
}

/** Polyline through points given in the glyph's 0..1 space. */
function poly(doc, x, y, s, pts) {
  for (let i = 0; i < pts.length - 1; i++) {
    doc.line(
      x + pts[i][0] * s,
      y + pts[i][1] * s,
      x + pts[i + 1][0] * s,
      y + pts[i + 1][1] * s,
    );
  }
}

const icoClock = (d, x, y, s) => {
  d.circle(x + 0.5 * s, y + 0.5 * s, 0.4 * s, "S");
  poly(d, x, y, s, [
    [0.5, 0.5],
    [0.5, 0.24],
  ]);
  poly(d, x, y, s, [
    [0.5, 0.5],
    [0.7, 0.6],
  ]);
};


const icoUsers = (d, x, y, s) => {
  d.circle(x + 0.36 * s, y + 0.32 * s, 0.16 * s, "S");
  poly(d, x, y, s, [
    [0.12, 0.84],
    [0.16, 0.58],
    [0.56, 0.58],
    [0.6, 0.84],
  ]);
  d.circle(x + 0.72 * s, y + 0.36 * s, 0.12 * s, "S");
  poly(d, x, y, s, [
    [0.66, 0.58],
    [0.88, 0.58],
    [0.92, 0.8],
  ]);
};

const icoInfo = (d, x, y, s) => {
  d.circle(x + 0.5 * s, y + 0.5 * s, 0.42 * s, "S");
  d.circle(x + 0.5 * s, y + 0.28 * s, 0.02 * s, "F");
  poly(d, x, y, s, [
    [0.5, 0.42],
    [0.5, 0.72],
  ]);
};

/** Small picture-frame glyph for the "Important Visuals" gallery header. */
const icoImage = (d, x, y, s) => {
  d.roundedRect(
    x + 0.08 * s,
    y + 0.14 * s,
    0.84 * s,
    0.72 * s,
    0.06 * s,
    0.06 * s,
    "S",
  );
  d.circle(x + 0.32 * s, y + 0.36 * s, 0.08 * s, "S");
  poly(d, x, y, s, [
    [0.14, 0.72],
    [0.4, 0.46],
    [0.58, 0.62],
    [0.76, 0.42],
    [0.92, 0.72],
  ]);
};

/** Dark rounded-square "chip" with a gold glyph — the confirmation page's motif. */
function iconChip(doc, x, y, size, glyph, opts = {}) {
  const bg = opts.bg || DEEP;
  const fg = opts.fg || GOLD;
  doc.setFillColor(...bg);
  doc.roundedRect(x, y, size, size, size * 0.28, size * 0.28, "F");
  stroke(doc, fg, Math.max(0.4, size * 0.055));
  doc.setFillColor(...fg);
  const inset = size * 0.28;
  glyph(doc, x + inset, y + inset, size - inset * 2);
}

function imageGlyph(dataUrl, format = "PNG", opts = {}) {
  const { inset = 0 } = opts; // extra inset beyond whatever the caller already applies

  return (doc, x, y, size) => {
    const drawSize = size - inset * 2;
    doc.addImage(dataUrl, format, x + inset, y + inset, drawSize, drawSize);
  };
}

async function loadImageAsDataURL(publicPath) {
  const res = await fetch(publicPath);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Loads every UI icon used across the pass exactly once. Fixed the missing
 * ".png" extension on "your-golfing-group-people", which would have 404'd
 * silently and produced an undefined data URI.
 */
async function loadIcons() {
  const [ticketBadge, flagIcon, gatewaySummary, yourGroup, itineraryClock] =
    await Promise.all([
      loadImageAsDataURL("/golfPassIcons/hero-ticket-badge.png"),
      loadImageAsDataURL("/golfPassIcons/your-getaway-flag.png"),
      loadImageAsDataURL("/golfPassIcons/your-getaway-summary-checklist.png"),
      loadImageAsDataURL("/golfPassIcons/your-golfing-group-people.png"),
      loadImageAsDataURL("/golfPassIcons/itinerary-clock.png"),
    ]);
  return { ticketBadge, flagIcon, gatewaySummary, yourGroup, itineraryClock };
}

/* ──────────────────────────────── main ──────────────────────────────────── */

/**
 * Builds the Golf Getaway Pass and returns the jsPDF document (no download).
 * @param {Record<string, any>} booking - booking / invoice-shaped object
 * @param {(string|{url:string,caption?:string})[]} [imageUrls] - gallery photos, optionally with captions
 * @param {string|string[]} [instructions] - admin's Instruction Text (block or array)
 * @param {string} [logoUrl]       - wordmark image URL/data URI; if omitted, the "EXQURA" text wordmark is used
 * @param {string} [timezoneNote]  - optional italic note shown under every day pill (e.g. a timezone-offset reminder)
 * @returns {Promise<import("jspdf").jsPDF>}
 */
export async function buildGolfPassDoc(
  booking,
  imageUrls,
  instructions,
  logoUrl,
) {
  const galleryItems = normalizeImages(imageUrls);
  const instructionLines = normalizeInstructions(instructions);
  // `compress: true` turns on jsPDF's built-in FlateDecode compression of the
  // PDF's internal streams (fonts, vector paths, etc). It won't touch image
  // bytes — that's handled separately by compressImageDataUri below — but it
  // meaningfully shrinks the rest of the document for free.
  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  doc.setProperties({
    title: `GolfGetawayPass-${booking.reservationNumber || booking.id || "pass"}`,
  });
  // Load every UI icon once up front instead of re-fetching the same PNGs
  // from three different places in this function.
  const icons = await loadIcons();

  const logoDataUri = logoUrl
    ? logoUrl.startsWith("data:")
      ? logoUrl
      : await compressImageDataUri(await urlToDataUri(logoUrl), 300, 0.8)
    : null;

  const pkg = booking.selectedPackage || {};
  const rounds = pkg.rounds ?? (booking.selectedCourses || []).length;
  const nights = Number(pkg.nights ?? 1);
  const days = nights + 1;
  const resNo = booking.reservationNumber || "—";

  let y = 0;
  const paintBg = () => {
    doc.setFillColor(...BG);
    doc.rect(0, 0, PAGE_W, PAGE_H, "F");
  };
  const newPage = () => {
    doc.addPage();
    paintBg();
    y = MARGIN;
  };
  const ensureSpace = (need) => {
    if (y + need > PAGE_H - FOOTER_H - 4) newPage();
  };

  paintBg();
  y = MARGIN;

  /* ───────────────────────── Hero ───────────────────────── */
  const HERO_H = 54;
  const cx = MARGIN + CONTENT_W / 2;
  doc.setFillColor(...DEEP);
  doc.roundedRect(MARGIN, y, CONTENT_W, HERO_H, 5, 5, "F");

  // Decorative translucent circles bleeding off both edges, clipped to the
  // hero card's rounded bounds so they never spill onto the page background.
  doc.saveGraphicsState();
  doc.roundedRect(MARGIN, y, CONTENT_W, HERO_H, 5, 5, null);
  doc.clip();
  doc.discardPath();
  doc.setGState(new doc.GState({ opacity: 0.06 }));
  doc.setFillColor(255, 255, 255);
  doc.circle(MARGIN, y + HERO_H * 0.32, 30, "F");
  doc.circle(MARGIN + CONTENT_W, y + HERO_H * 0.88, 26, "F");
  doc.restoreGraphicsState();

  // "EXQURA" wordmark, top-right of the hero
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...GOLD);
  doc.setCharSpace(0.6);
  const wordmarkExtra = 0.6 * ("EXQURA".length - 1);
  doc.text("EXQURA", MARGIN + CONTENT_W - 8 - wordmarkExtra, y + 9, {
    align: "right",
  });
  doc.setCharSpace(0);

  // Gold ticket badge — background rect, same as iconChip drew
  const badgeSize = 13;
  const badgeX = cx - badgeSize / 2;
  const badgeY = y + 7;

  doc.setFillColor(...GOLD);
  doc.roundedRect(
    badgeX,
    badgeY,
    badgeSize,
    badgeSize,
    badgeSize * 0.28,
    badgeSize * 0.28,
    "F",
  );

  // Image on top, inset the same way iconChip inset its glyph — reuses the
  // already-loaded ticket badge instead of fetching it again.
  const inset = badgeSize * 0.28;
  doc.addImage(
    icons.ticketBadge,
    "PNG",
    badgeX + inset,
    badgeY + inset,
    badgeSize - inset * 2,
    badgeSize - inset * 2,
  );

  // Title
  setFont(doc, "bold", 21, [255, 255, 255]);
  doc.setCharSpace(0.15);
  doc.text("Golf Getaway Pass", cx, y + 29, { align: "center" });
  doc.setCharSpace(0);

  // Reservation pill — outlined, "Reservation No:" label + bold value
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  const resLabel = "Reservation No: ";
  const resLabelW = doc.getTextWidth(resLabel);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  const resValW = doc.getTextWidth(resNo);
  const pillPadX = 7.5;
  const pillW = pillPadX * 2 + resLabelW + resValW;
  const pillX = cx - pillW / 2;
  const pillY = y + 35;
  const pillH = 8.6;
  doc.setFillColor(...PILL_BG);
  doc.setDrawColor(...PILL_BORDER);
  doc.setLineWidth(0.3);
  doc.roundedRect(pillX, pillY, pillW, pillH, pillH / 2, pillH / 2, "FD");
  doc.setTextColor(220, 224, 220);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.text(resLabel, pillX + pillPadX, pillY + pillH / 2 + 1.1);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.text(resNo, pillX + pillPadX + resLabelW, pillY + pillH / 2 + 1.1);

  // "Date Issued" line, below the pill
  setFont(doc, "normal", 8, [210, 214, 210]);
  // Issue date is fixed at the first send; re-sends keep the original date.
  doc.text(`Date Issued: ${formatShort(booking.passIssuedAt || new Date())}`, cx, pillY + pillH + 6, {
    align: "center",
  });

  y += HERO_H + GAP;

  /* ─────────────────── Your Getaway / Your Getaway Summary ──────────────────
   * Two cards side by side. Left: headline Days/Nights. Right: a plain-
   * English bullet summary of what's included. */
  const summaryLines = [];
  summaryLines.push(`${rounds} Round${rounds > 1 ? "s" : ""} of 18 Holes Golf`);
  if (booking.selectedAccommodation) {
    summaryLines.push(
      `${nights} Night${nights > 1 ? "s" : ""} Stay @ ${booking.selectedAccommodation.title || "Hotel"}`,
    );
  }
  if (booking.selectedFerry) {
    summaryLines.push(
      `Return Trip Ferry via ${booking.selectedFerry.title || "Ferry"}`,
    );
  }
  if (booking.selectedTransport) {
    summaryLines.push("Land Transfers");
  }

  const twoColW = (CONTENT_W - GAP) / 2;
  const getawayH = PAD + 7 + 2 * LH(15) + 4 + (PAD - 3);
  const summaryBodyH = summaryLines.reduce(
    (acc, l) => acc + wrap(doc, l, twoColW - 12, 8).length * LH(8) + 2,
    0,
  );
  const summaryH = PAD + 7 + summaryBodyH + (PAD - 3);
  const statH = Math.max(getawayH, summaryH, 30);
  ensureSpace(statH);

  // "Your Getaway"
  {
    const x = MARGIN + PAD;
    panel(doc, MARGIN, y, twoColW, statH);
    const iconGetway = imageGlyph(icons.flagIcon, "PNG");
    iconChip(doc, x - 1, y + 5.5, 8, iconGetway);
    eyebrow(doc, "Your Getaway", x + 10.5, y + 10.5);
    setFont(doc, "bold", 17, DEEP);
    doc.text(`${days} Day${days > 1 ? "s" : ""}`, x, y + 24);
    doc.text(`${nights} Night${nights > 1 ? "s" : ""}`, x, y + 24 + LH(15));
  }
  ensureSpace(10);
  // "Your Getaway Summary"
  {
    const x2 = MARGIN + twoColW + GAP + PAD;
    panel(doc, MARGIN + twoColW + GAP, y, twoColW, statH);
    const getwaySum = imageGlyph(icons.gatewaySummary, "PNG");
    iconChip(doc, x2 - 1, y + 5.5, 8, getwaySum, { fg: [255, 255, 255] });
    eyebrow(doc, "Your Getaway Summary", x2 + 10.5, y + 10.5);
    let sy = y + 18.5;
    summaryLines.forEach((l) => {
      const lines = wrap(doc, l, twoColW - 2 * PAD - 4, 8);
      setFont(doc, "bold", 9, INK);
      drawLines(doc, lines, x2, sy, 8);
      sy += lines.length * LH(8) + 2;
    });
  }

  y += statH + GAP;

  /* ───────────────────────── Your Golfing Group ─────────────────────────
   * Tinted card, one golfer name per line — lead golfer flagged inline. */
  {
    const roster =
      Array.isArray(booking.roster) && booking.roster.length
        ? booking.roster
        : (Array.isArray(booking.golferNames)
            ? booking.golferNames
            : Array.isArray(booking.players)
              ? booking.players
              : []
          ).map((p) => (typeof p === "string" ? { name: p } : p));

    const cleaned = roster
      .map((g) => ({ ...g, name: String(g?.name ?? "").trim() }))
      .filter((g) => g.name);

    const leadEntry = cleaned.find((g) => g.isLead);
    const lead = (
      leadEntry?.name ||
      booking.customerName ||
      cleaned[0]?.name ||
      "Lead Golfer"
    ).trim();
    const others = cleaned
      .filter((g) => g.name.toLowerCase() !== lead.toLowerCase())
      .map((g) => g.name);
    const x = MARGIN + PAD;
    const innerW = CONTENT_W - 2 * PAD;

    const noteLine =
      "Every golfer will receive their own copy of this Getaway Pass.";
    const noteLines = wrap(doc, noteLine, innerW, 7.5, "italic");

    const rowsH = (1 + others.length) * LH(9.5) + 3;
    const h = PAD + 7 + 5 + rowsH + 3 + noteLines.length * LH(7.5) + (PAD - 3);
    ensureSpace(h);
    card(doc, y, h, { fill: TINT, border: TINT_BORDER });
    iconChip(doc, x, y + PAD - 0.5, 7, icoUsers);
    eyebrow(doc, "Your Golfing Group", x + 11, y + PAD + 3.4);

    let gy = y + PAD + 7 + 6;
    setFont(doc, "bold", 9.5, DEEP);
    doc.text(`${lead} [Lead Golfer]`, x, gy);
    others.forEach((n) => {
      gy += LH(9.5) + 1.5;
      setFont(doc, "bold", 9.5, DEEP);
      doc.text(n, x, gy);
    });
    gy += LH(9.5) + 3.5;
    setFont(doc, "italic", 7.5, GRAY_SOFT);
    drawLines(doc, noteLines, x, gy, 7.5);
    y += h + GAP;
  }

  /* ───────────────────────── Golf Rounds ───────────────────────── */
  // if (courses.length) {
  //   const x = MARGIN + PAD;
  //   const rowH = 9.5;
  //   const headerBlock = 13;
  //   const h = PAD + headerBlock + (courses.length - 1) * rowH + 4 + (PAD - 2);
  //   ensureSpace(h);
  //   card(doc, y, h);

  //   iconChip(doc, x, y + PAD, 8, imageGlyph(icons.flagIcon, "PNG"));
  //   eyebrow(doc, "Golf Rounds", x + 11.5, y + PAD + 5.2);
  //   let ry = y + PAD + headerBlock;
  //   courses.forEach((co, i) => {
  //     doc.setFillColor(...DEEP);
  //     doc.circle(x + 2.8, ry - 1.5, 2.8, "F");
  //     doc.setFont("helvetica", "bold");
  //     doc.setFontSize(7);
  //     doc.setTextColor(...GOLD);
  //     doc.text(String(i + 1), x + 2.8, ry - 1.5, { align: "center", baseline: "middle" });
  //     setFont(doc, "bold", 9, DEEP);
  //     doc.text(String(co.name || "Golf Course"), x + 9, ry - 0.6);
  //     const meta = [
  //       formatShort(booking.roundDates?.[i]),
  //       booking.roundTimes?.[i] ? `${booking.roundTimes[i]} tee` : null,
  //     ].filter(Boolean);
  //     if (meta.length) {
  //       setFont(doc, "normal", 7.5, GRAY);
  //       doc.text(meta.join("   ·   "), x + 9, ry + 3.2);
  //     }
  //     ry += rowH;
  //   });
  //   y += h + GAP;
  // }

  /* ───────────────────────── Itinerary ─────────────────────────
   * One card per day: a dark day pill ("Day One: Friday, 04 Sep 2026"),
   * an optional italic timezone note, then bold-type/plain-desc rows. */
  const itinerary = buildItinerary(booking);
  if (itinerary.length) {
    const x = MARGIN + PAD;
    const labelX = x + 22;
    const labelW = MARGIN + CONTENT_W - PAD - labelX;
    const HEADER_H = 10; // extra room the "ITINERARY" icon + eyebrow takes at the top of the first day card
    itinerary.forEach((day, dayIdx) => {
      const isFirst = dayIdx === 0;
      const isLast = dayIdx === itinerary.length - 1;

      const timezoneNote =
        "Batam Time (ID) is one hour behind Singapore Time (SG)";

      const noteLines = timezoneNote
        ? wrap(doc, timezoneNote, CONTENT_W - 2 * PAD, 7.5, "italic")
        : [];

      const rowLineCounts = day.items.map((it) =>
        measureTypeDescLines(doc, it.type, it.desc, labelW, 8.5),
      );

      const HEAD =
        15 +
        (noteLines.length ? noteLines.length + 3 * LH(7.5) + 2 : 0) +
        (isFirst ? HEADER_H : 0);

      const ROW_GAP = 2.6;

      // Footer note only on the last card
      const FOOTER_NOTE = isLast ? LH(6.5) + 3 : 0;

      let h = HEAD;

      rowLineCounts.forEach((n, i) => {
        h +=
          Math.max(n * LH(8.5), 4.6) +
          (i < rowLineCounts.length - 1 ? ROW_GAP : 0);
      });

      h += 2.5 + FOOTER_NOTE;

      ensureSpace(h);

      card(doc, y, h, {
        fill: GREEN_TINT,
        border: GREEN_BORDER,
      });

      let pillTop = y + 5.5;

      if (isFirst) {
        stroke(doc, GOLD, 0.45);
        icoClock(doc, x, y + PAD - 0.5, 7);

        eyebrow(doc, "Itinerary", x + 11, y + PAD + 3.4);
        pillTop = y + PAD + 7 + 4;
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(6.6);
      doc.setCharSpace(0.25);

      const label = day.dayLabel.toUpperCase();
      const pillH = 7.4;
      const pw = doc.getTextWidth(label) + label.length * 0.25 + 10;

      doc.setFillColor(...DEEP);
      doc.roundedRect(x, pillTop, pw, pillH, pillH / 2, pillH / 2, "F");

      doc.setTextColor(...GOLD);
      doc.text(label, x + 5, pillTop + pillH / 2 + 1);
      doc.setCharSpace(0);

      let iy = pillTop + pillH + 5;

      if (noteLines.length) {
        const NOTE_TOP_MARGIN = 3;

        iy += NOTE_TOP_MARGIN;

        setFont(doc, "italic", 7.5, GRAY);
        drawLines(doc, noteLines, x, iy - 2, 7.5);

        iy += noteLines.length * LH(7.5) + 2;
      }

      day.items.forEach((it, i) => {
        setFont(doc, "bold", 8, DEEP);
        doc.text(it.time || "—", x, iy);

        const lines = drawTypeDesc(
          doc,
          it.type,
          it.desc,
          labelX,
          iy,
          labelW,
          8.5,
        );

        iy += Math.max(lines * LH(8.5), 4.6);

        if (i < day.items.length - 1) {
          iy += ROW_GAP;
        }
      });

      // Footer note inside the last itinerary card
      if (isLast) {
        const footerY = y + h - 4;

        setFont(doc, "italic", 6.5, GRAY);

        doc.text(
          "Please note, all timings stated are approximate and may vary depending on daily conditions",
          x,
          footerY,
          {
            maxWidth: CONTENT_W - 2 * PAD,
          },
        );
      }

      y += h + (isLast ? GAP : 4);
    });
  }

  /* ─────────────── Travel & Stay (ferry / transport / accommodation) ────────── */
  // const stayRows = [];
  // if (booking.selectedFerry) {
  //   const f = booking.selectedFerry;
  //   const route = [f.originport, f.destination].filter(Boolean).join("  ·  ");
  //   const times = [
  //     booking.selectedFerryTime && `Depart ${wib(booking.selectedFerryTime)}`,
  //     booking.selectedOutboundFerryTime &&
  //       `Return ${wib(booking.selectedOutboundFerryTime)}`,
  //   ]
  //     .filter(Boolean)
  //     .join("      ");
  //   stayRows.push({
  //     glyph: icoShip,
  //     label: "Ferry · Round Trip",
  //     title: f.title || "Ferry",
  //     subs: [route || null, times || null],
  //   });
  // }

  // if (booking.selectedTransport) {
  //   const tr = booking.selectedTransport;
  //   stayRows.push({
  //     glyph: icoCar,
  //     label: "Land Transport",
  //     title: `${tr.title || "Transfer"}${tr.type ? ` · ${tr.type}` : ""}`,
  //     subs: [
  //       booking.selectedTransportTime &&
  //         `Pickup ${wib(booking.selectedTransportTime)}`,
  //       booking.selectedOutboundTransportTime &&
  //         `Return ${wib(booking.selectedOutboundTransportTime)}${
  //           booking.selectedOutboundTransport
  //             ? ` · ${booking.selectedOutboundTransport.title}`
  //             : ""
  //         }`,
  //     ].filter(Boolean),
  //   });
  // }
  // if (booking.selectedAccommodation) {
  //   const a = booking.selectedAccommodation;
  //   stayRows.push({
  //     glyph: icoBed,
  //     label: "Accommodation",
  //     title: a.title || "Hotel",
  //     subs: [
  //       booking.checkInDate && booking.checkOutDate
  //         ? `${formatShort(booking.checkInDate, false)} – ${formatShort(booking.checkOutDate)} · ${nights} night${nights > 1 ? "s" : ""}`
  //         : `${nights} night${nights > 1 ? "s" : ""}`,
  //       booking.checkInTime ? `Check-in ${wib(booking.checkInTime)}` : null,
  //     ].filter(Boolean),
  //   });
  // }

  // if (stayRows.length) {
  //   const x = MARGIN + PAD;
  //   const glyphSize = 7;
  //   const contentX = x + glyphSize + 4;
  //   const rowInnerW = MARGIN + CONTENT_W - PAD - contentX;
  //   const measured = stayRows.map((r) => {
  //     const titleLines = r.title
  //       ? wrap(doc, r.title, rowInnerW, 9.5, "bold")
  //       : [];
  //     const subLines = (r.subs || [])
  //       .filter(Boolean)
  //       .map((s) => wrap(doc, s, rowInnerW, 8));
  //     let contentH = 5.5;
  //     contentH += titleLines.length * LH(9.5);
  //     subLines.forEach((sl) => (contentH += sl.length * LH(8) + 0.8));
  //     return {
  //       titleLines,
  //       subLines,
  //       rh: Math.max(contentH, glyphSize + 2) + 6,
  //     };
  //   });

  //   let h = PAD + 7;
  //   measured.forEach((m) => (h += m.rh));
  //   h += PAD - 5;

  //   ensureSpace(h);
  //   card(doc, y, h);
  //   eyebrow(doc, "Travel & Stay", x, y + PAD + 1);

  //   let ry = y + PAD + 9;
  //   measured.forEach((m, idx) => {
  //     const r = stayRows[idx];
  //     if (idx > 0) {
  //       doc.setDrawColor(...BORDER);
  //       doc.setLineWidth(0.25);
  //       doc.line(x, ry - 4, MARGIN + CONTENT_W - PAD, ry - 4);
  //     }
  //     iconChip(doc, x, ry - 1, glyphSize, r.glyph);
  //     eyebrow(doc, r.label, contentX, ry + 2.6);
  //     let ty = ry + 8;
  //     if (m.titleLines.length) {
  //       setFont(doc, "bold", 9.5, DEEP);
  //       drawLines(doc, m.titleLines, contentX, ty, 9.5);
  //       ty += m.titleLines.length * LH(9.5) + 0.8;
  //     }
  //     setFont(doc, "normal", 8, GRAY);
  //     m.subLines.forEach((sl) => {
  //       drawLines(doc, sl, contentX, ty, 8);
  //       ty += sl.length * LH(8) + 0.8;
  //     });
  //     ry += m.rh;
  //   });
  //   y += h + GAP;
  // }

  if (itinerary.length === 1) newPage();

  /* ───────────────────────── Important Instructions ───────────────────────── */
  {
    const x = MARGIN + PAD;
    const headH = 12;
    const innerW = CONTENT_W - 2 * PAD - 9;
    const wrapped = instructionLines.map((l) => wrap(doc, l, innerW, 8.5));

    let bodyH = 7;
    wrapped.forEach((w) => (bodyH += Math.max(w.length * LH(8.5), 5) + 3));
    bodyH += 1.5;
    const h = headH + bodyH + 2;

    ensureSpace(h);
    card(doc, y, h);
    doc.setFillColor(...DEEP);
    doc.roundedRect(MARGIN, y, CONTENT_W, headH, RADIUS, RADIUS, "F");
    doc.rect(MARGIN, y + headH - RADIUS, CONTENT_W, RADIUS, "F");
    stroke(doc, [255, 255, 255], 0.5);
    doc.setFillColor(255, 255, 255);
    icoInfo(doc, x, y + headH / 2 - 3, 6);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(255, 255, 255);
    doc.setCharSpace(0.35);
    doc.text("IMPORTANT INSTRUCTIONS", x + 10, y + headH / 2 + 1.1);
    doc.setCharSpace(0);

    let ny = y + headH + 7;
    wrapped.forEach((w, i) => {
      doc.setFillColor(...GOLD);
      doc.circle(x + 2.6, ny - 1.6, 2.6, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6.8);
      doc.setTextColor(...DEEP);
      doc.text(String(i + 1), x + 2.6, ny - 0.4, { align: "center" });
      setFont(doc, "normal", 8.5, DEEP);
      drawLines(doc, w, x + 9, ny, 8.5);
      ny += Math.max(w.length * LH(8.5), 5) + 3;
    });
    y += h + GAP;
  }

  /* ───────────────────────── Important Visuals (photo gallery) ─────────────────
   * Dark header bar (matches the Instructions card), then a single row of
   * up to 5 photos each with a caption underneath. Photos are downscaled +
   * re-encoded as JPEG before embedding — this is normally the single
   * biggest contributor to file size, since source photos are often several
   * megapixels while they only ever render inside a small ~30×53mm box. */
  const galleryImgs = await Promise.all(
    galleryItems.map(async (item) => {
      const raw = await urlToDataUri(item.url);
      const dataUri = await compressImageDataUri(raw, 480, 0.72);
      return { dataUri, caption: item.caption };
    }),
  );

  const galleryValid = galleryImgs.filter((g) => g.dataUri);
  if (galleryValid.length) {
    const n = Math.min(galleryValid.length, 5);
    const x = MARGIN + PAD;
    const headH = 12;
    const gh = 30;
    const gGap = 4;
    const iw = (CONTENT_W - 2 * PAD - gGap * (n - 1)) / n;

    // Wrap every caption up to 2 lines so longer captions aren't cut off
    // after the first line. The tallest caption in the row sets how much
    // room the whole gallery card reserves below the photos.
    const captionLineSets = galleryValid
      .slice(0, n)
      .map((g) => (g.caption ? wrap(doc, g.caption, iw, 7).slice(0, 2) : []));
    const maxCaptionLines = Math.max(
      0,
      ...captionLineSets.map((l) => l.length),
    );
    const captionH = maxCaptionLines ? maxCaptionLines * LH(7) + 3 : 0;

    const h = headH + PAD + gh + captionH + (PAD - 3);

    ensureSpace(h);
    card(doc, y, h);
    doc.setFillColor(...DEEP);
    doc.roundedRect(MARGIN, y, CONTENT_W, headH, RADIUS, RADIUS, "F");
    doc.rect(MARGIN, y + headH - RADIUS, CONTENT_W, RADIUS, "F");
    stroke(doc, [255, 255, 255], 0.5);
    doc.setFillColor(255, 255, 255);
    icoImage(doc, x, y + headH / 2 - 3, 6);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(255, 255, 255);
    doc.setCharSpace(0.35);
    doc.text("IMPORTANT VISUALS", x + 10, y + headH / 2 + 1.1);
    doc.setCharSpace(0);

    const gy = y + headH + PAD;
    for (let i = 0; i < n; i++) {
      const gx = x + i * (iw + gGap);
      doc.setDrawColor(...BORDER);
      doc.setLineWidth(0.3);
      doc.roundedRect(gx, gy, iw, gh, 2, 2, "S");
      try {
        addContainedImage(
          doc,
          galleryValid[i].dataUri,
          gx + 0.8,
          gy + 0.8,
          iw - 1.6,
          gh - 1.6,
        );
      } catch {
        /* skip an image that fails to embed rather than abort the PDF */
      }
      const lines = captionLineSets[i];
      if (lines.length) {
        setFont(doc, "normal", 7, GRAY);
        lines.forEach((ln, li) => {
          doc.text(ln, gx + iw / 2, gy + gh + 4.5 + li * LH(7), {
            align: "center",
          });
        });
      }
    }
    y += h + GAP;
  }

  addFooters(doc, logoDataUri);
  return doc;
}

/**
 * Generates the Golf Getaway Pass PDF. By default it triggers a browser
 * download; pass an `opts` object to get the PDF back instead:
 *   - `{ returnBase64: true }`  → resolves to the raw base64 string (no data-URI
 *      prefix), for handing to a cloud function that emails the attachment.
 *   - `{ returnBlobUrl: true }` → resolves to an object URL you can open in a new
 *      tab or drop into an <iframe> to view the pass without saving a file.
 *      Remember to URL.revokeObjectURL(url) when you're done with it.
 * @param {Record<string, any>} booking - booking / invoice-shaped object
 * @param {(string|{url:string,caption?:string})[]} [imageUrls] - gallery photos, optionally with captions
 * @param {string|string[]} [instructions] - admin's Instruction Text (block or array)
 * @param {string} [logoUrl]       - wordmark image URL/data URI; if omitted, the "EXQURA" text wordmark is used
 * @param {string} [timezoneNote]  - optional italic note shown under every day pill
 * @param {{ returnBase64?: boolean, returnBlobUrl?: boolean }} [opts]
 * @returns {Promise<string|void>}
 */
export async function generateGolfPass(
  booking,
  imageUrls,
  instructions,
  logoUrl,
  timezoneNote,
  opts = {},
) {
  const doc = await buildGolfPassDoc(
    booking,
    imageUrls,
    instructions,
    logoUrl,
    timezoneNote,
  );

  if (opts.returnBase64) {
    // "datauristring" → "data:application/pdf;base64,JVBERi0…" — strip the prefix.
    return doc.output("datauristring").split(",")[1];
  }
  if (opts.returnBlobUrl) {
    return URL.createObjectURL(doc.output("blob"));
  }

  doc.save(
    `GolfGetawayPass-${booking.reservationNumber || booking.id || "pass"}.pdf`,
  );
}

/** Full-bleed dark footer band on every page — wordmark, support contact, page no. */
function addFooters(doc, logoDataUri) {
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    const by = PAGE_H - FOOTER_H;
    const mid = by + FOOTER_H / 2 + 1;

    doc.setFillColor(...DEEP);
    doc.rect(0, by, PAGE_W, FOOTER_H, "F");

    if (logoDataUri) {
      try {
        const p = doc.getImageProperties(logoDataUri);
        const lw = 22;
        const lh = lw * (p.height / p.width);
        doc.addImage(
          logoDataUri,
          dataUriFormat(logoDataUri),
          MARGIN,
          by + (FOOTER_H - lh) / 2,
          lw,
          lh,
        );
      } catch {
        /* fall through to the text wordmark below */
      }
    } else {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...GOLD);
      doc.setCharSpace(1);
      doc.text("EXQURA", MARGIN, mid + 1);
      doc.setCharSpace(0);
    }

    const question = "Questions or need to make a change?  ";
    const email = "customerservice@exqura.golf";
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    const qw = doc.getTextWidth(question);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    const ew = doc.getTextWidth(email);
    const sx = (PAGE_W - (qw + ew)) / 2;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(255, 255, 255);
    doc.text(question, sx, mid);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...GOLD);
    doc.text(email, sx + qw, mid);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(255, 255, 255);
    doc.text(`${i} / ${pages}`, PAGE_W - MARGIN, mid, { align: "right" });
  }
}
